import { cn } from "@/lib/utils";
import { Trophy, Check, X, Minus } from "lucide-react";

const PLACEHOLDER_FIGHTER = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";

export default function FightCard({ fight, selectedWinner, onSelectWinner, fightNumber, userPick }) {
  const { fighter1, fighter2, weight_class, fight_type, is_main_event, result, result_method, is_locked } = fight;
  const isDecided = !!result;
  const isDraw = result === 'draw';
  const isDisabled = isDecided || is_locked;

  // User pick result indicator
  const hasUserPick = !!userPick;
  const pickIsCorrect = userPick?.is_correct;
  const pickIsDraw = userPick?.is_draw;

  // Border color based on user pick result
  let borderClass = is_main_event ? "border-[#FF3B30]/50" : "border-white/10";
  if (hasUserPick) {
    if (pickIsCorrect === true) borderClass = "border-green-500/50";
    else if (pickIsCorrect === false) borderClass = "border-red-500/50";
    else if (pickIsDraw) borderClass = "border-white/20";
  } else if (isDecided) {
    borderClass = "border-white/20";
  } else if (is_locked && !isDecided) {
    borderClass = "border-yellow-500/30";
  }

  return (
    <div 
      className={cn(
        "relative bg-[#121212] border overflow-hidden group transition-colors flex flex-col h-full",
        borderClass
      )}
      data-testid={`fight-card-${fight.id}`}
    >
      {/* User pick result badge */}
      {hasUserPick && pickIsCorrect === true && (
        <div className="absolute top-2 right-2 z-10 w-7 h-7 bg-green-500 flex items-center justify-center" data-testid={`pick-correct-${fight.id}`}>
          <Check className="w-4 h-4 text-white" />
        </div>
      )}
      {hasUserPick && pickIsCorrect === false && (
        <div className="absolute top-2 right-2 z-10 w-7 h-7 bg-red-500 flex items-center justify-center" data-testid={`pick-wrong-${fight.id}`}>
          <X className="w-4 h-4 text-white" />
        </div>
      )}
      {hasUserPick && pickIsDraw && (
        <div className="absolute top-2 right-2 z-10 w-7 h-7 bg-white/20 flex items-center justify-center" data-testid={`pick-draw-${fight.id}`}>
          <Minus className="w-4 h-4 text-white" />
        </div>
      )}
      {hasUserPick && pickIsCorrect === null && !pickIsDraw && (
        <div className="absolute top-2 right-2 z-10 px-2 py-0.5 bg-white/10 text-[9px] uppercase tracking-wider text-white/50 font-bold"
          data-testid={`pick-pending-${fight.id}`}>
          Odottaa
        </div>
      )}

      {/* Top strip */}
      <div className="h-5 shrink-0">
        {is_main_event ? (
          <div className="bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-wider py-0.5 text-center h-full flex items-center justify-center">
            Pääottelu
          </div>
        ) : (
          <div className="h-full" />
        )}
      </div>

      {/* Fight Info */}
      <div className="px-3 pt-1 pb-1 text-center space-y-0.5 shrink-0">
        {fightNumber != null && (
          <span className="text-[10px] uppercase tracking-[0.15em] text-white/40 font-bold block">
            Ottelu {fightNumber}
          </span>
        )}
        {fight_type && (
          <span className="text-[9px] uppercase tracking-[0.2em] text-[#FF3B30] font-bold block">{fight_type}</span>
        )}
        <span className="text-[9px] uppercase tracking-[0.2em] text-[#A1A1AA] font-bold block">{weight_class}</span>
      </div>

      {/* Result Banner or Locked Banner */}
      <div className="shrink-0 h-9">
        {isDraw ? (
          <div className="bg-white/5 border-y border-white/10 px-3 py-1.5 text-center h-full flex items-center justify-center">
            <span className="text-xs font-bold text-white/60 uppercase">Tasapeli</span>
            {result_method && <span className="text-[10px] text-white/40 ml-2">({result_method})</span>}
          </div>
        ) : isDecided ? (
          <div className="bg-white/5 border-y border-white/10 px-3 py-1.5 text-center h-full flex flex-col items-center justify-center">
            <div className="flex items-center justify-center gap-1.5">
              <Trophy className="w-3 h-3 text-white" />
              <span className="text-xs font-bold text-white uppercase">
                {result === 'fighter1' ? fighter1.name : fighter2.name}
              </span>
            </div>
            {result_method && (
              <p className="text-[10px] lg:text-xs text-white/50 leading-none">{result_method}</p>
            )}
          </div>
        ) : is_locked ? (
          <div className="bg-yellow-500/10 border-y border-yellow-500/20 px-3 py-1.5 text-center h-full flex items-center justify-center">
            <span className="text-[10px] font-bold text-yellow-500 uppercase tracking-wider">Ottelu käynnissä</span>
          </div>
        ) : (
          <div className="h-full" />
        )}
      </div>

      {/* Fighters */}
      <div className="flex items-stretch flex-1">
        {/* Fighter 1 */}
        <button
          onClick={() => !isDisabled && !hasUserPick && onSelectWinner('fighter1')}
          disabled={isDisabled || hasUserPick}
          className={cn(
            "flex-1 p-2 pt-2 pb-3 transition-all duration-200 border-r border-white/5 flex items-center justify-center",
            !isDisabled && !hasUserPick && selectedWinner === 'fighter1' && "bg-white/10",
            !isDisabled && !hasUserPick && selectedWinner !== 'fighter1' && "hover:bg-white/5",
            hasUserPick && selectedWinner === 'fighter1' && pickIsCorrect === true && "bg-green-500/10",
            hasUserPick && selectedWinner === 'fighter1' && pickIsCorrect === false && "bg-red-500/10",
            hasUserPick && selectedWinner === 'fighter1' && pickIsDraw && "bg-white/5",
            hasUserPick && selectedWinner === 'fighter1' && pickIsCorrect === null && !pickIsDraw && "bg-white/5",
            isDecided && result === 'fighter1' && !hasUserPick && "bg-white/5",
            isDecided && result !== 'fighter1' && !isDraw && !hasUserPick && "opacity-40",
            isDraw && !hasUserPick && "opacity-60"
          )}
          data-testid={`select-fighter1-${fight.id}`}
          data-selected={selectedWinner === 'fighter1'}
        >
          <div className="text-center">
            <div className={cn(
              "w-20 h-20 mx-auto mb-2 overflow-hidden rounded-full",
              !isDisabled && !hasUserPick && selectedWinner === 'fighter1' && "ring-2 ring-white",
              hasUserPick && selectedWinner === 'fighter1' && pickIsCorrect === true && "ring-2 ring-green-500",
              hasUserPick && selectedWinner === 'fighter1' && pickIsCorrect === false && "ring-2 ring-red-500",
              hasUserPick && selectedWinner === 'fighter1' && (pickIsDraw || pickIsCorrect === null) && "ring-2 ring-white/40",
              isDecided && result === 'fighter1' && !hasUserPick && "ring-2 ring-white"
            )}>
              <img src={fighter1.image_url || PLACEHOLDER_FIGHTER} alt={fighter1.name}
                className={`w-full h-full ${fighter1.image_url ? "object-cover object-top grayscale" : "object-contain p-3 opacity-30"}`} />
            </div>
            <h3 className="text-sm lg:text-base uppercase tracking-tight leading-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {fighter1.name}
            </h3>
            {fighter1.hometown && (
              <p className="text-[9px] lg:text-[10px] uppercase tracking-wider text-[#A1A1AA]">{fighter1.hometown}</p>
            )}
            {fighter1.record && (
              <p className="text-[10px] lg:text-xs text-white/50">{fighter1.record}</p>
            )}
            {hasUserPick && selectedWinner === 'fighter1' && (
              <p className="text-[9px] uppercase tracking-wider text-white/50 mt-1 font-bold">Veikkauksesi</p>
            )}
          </div>
        </button>

        {/* VS */}
        <div className="flex items-center justify-center w-8 shrink-0">
          <span className="text-xs text-white/30 font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>VS</span>
        </div>

        {/* Fighter 2 */}
        <button
          onClick={() => !isDisabled && !hasUserPick && onSelectWinner('fighter2')}
          disabled={isDisabled || hasUserPick}
          className={cn(
            "flex-1 p-2 pt-2 pb-3 transition-all duration-200 border-l border-white/5 flex items-center justify-center",
            !isDisabled && !hasUserPick && selectedWinner === 'fighter2' && "bg-white/10",
            !isDisabled && !hasUserPick && selectedWinner !== 'fighter2' && "hover:bg-white/5",
            hasUserPick && selectedWinner === 'fighter2' && pickIsCorrect === true && "bg-green-500/10",
            hasUserPick && selectedWinner === 'fighter2' && pickIsCorrect === false && "bg-red-500/10",
            hasUserPick && selectedWinner === 'fighter2' && pickIsDraw && "bg-white/5",
            hasUserPick && selectedWinner === 'fighter2' && pickIsCorrect === null && !pickIsDraw && "bg-white/5",
            isDecided && result === 'fighter2' && !hasUserPick && "bg-white/5",
            isDecided && result !== 'fighter2' && !isDraw && !hasUserPick && "opacity-40",
            isDraw && !hasUserPick && "opacity-60"
          )}
          data-testid={`select-fighter2-${fight.id}`}
          data-selected={selectedWinner === 'fighter2'}
        >
          <div className="text-center">
            <div className={cn(
              "w-20 h-20 mx-auto mb-2 overflow-hidden rounded-full",
              !isDisabled && !hasUserPick && selectedWinner === 'fighter2' && "ring-2 ring-white",
              hasUserPick && selectedWinner === 'fighter2' && pickIsCorrect === true && "ring-2 ring-green-500",
              hasUserPick && selectedWinner === 'fighter2' && pickIsCorrect === false && "ring-2 ring-red-500",
              hasUserPick && selectedWinner === 'fighter2' && (pickIsDraw || pickIsCorrect === null) && "ring-2 ring-white/40",
              isDecided && result === 'fighter2' && !hasUserPick && "ring-2 ring-white"
            )}>
              <img src={fighter2.image_url || PLACEHOLDER_FIGHTER} alt={fighter2.name}
                className={`w-full h-full ${fighter2.image_url ? "object-cover object-top grayscale" : "object-contain p-3 opacity-30"}`} />
            </div>
            <h3 className="text-sm lg:text-base uppercase tracking-tight leading-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              {fighter2.name}
            </h3>
            {fighter2.hometown && (
              <p className="text-[9px] lg:text-[10px] uppercase tracking-wider text-[#A1A1AA]">{fighter2.hometown}</p>
            )}
            {fighter2.record && (
              <p className="text-[10px] lg:text-xs text-white/50">{fighter2.record}</p>
            )}
            {hasUserPick && selectedWinner === 'fighter2' && (
              <p className="text-[9px] uppercase tracking-wider text-white/50 mt-1 font-bold">Veikkauksesi</p>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}
